import { isTagSelector } from "./cfSelectionMode";
import type { ResolvedField } from "@/features/customField/api/useResolveCustomDefaults";

/**
 * Client-side twin of the server's `bridgeCountryByValue` (STEP 5 of
 * `resolveCustomFieldDefaults`).
 *
 * A deal's Country must follow the picked course → its university (and, in the
 * Attach Course modal, the Country picked in the left-hand filter). The server
 * resolver locks it when it can, but tenants routinely keep SEPARATE Country
 * fields for universities and deals (Sterling), and the resolver only learned
 * to bridge those on 2026-09-08. The picked course already carries its
 * university's Country VALUE ("Uk") in the client, so the lock is derived here
 * too — no round trip, and it stays locked even against a server that has not
 * caught up.
 *
 * HOW THE FIELD IS IDENTIFIED (rewritten 2026-09-09 — the lock had failed on
 * real tenants three times running). Two passes, in order:
 *
 *   1. DECLARED — `spesificTypes === "Country"`, or the field's `name`
 *      CONTAINS the word "country" ("Preferred Country", "Country of
 *      Interest"). This used to demand `name === "Country"` exactly, which is
 *      why nothing locked: the stage editor's `label` overlay let a field
 *      read "Country" on screen while its stored `name` was something else,
 *      and `label` must never be used for matching (see cfDisplayName).
 *   2. BY ITS OPTIONS — no declared field matched, so find the field whose
 *      OPTIONS contain the country we are looking for. Only a single-select
 *      field with EXACTLY ONE matching option counts, and only when exactly
 *      ONE field across the form qualifies; two candidates is ambiguous and
 *      locks nothing. The value being matched comes from the course
 *      catalogue's own country facet, so an option equal to it is strong
 *      evidence — and this pass needs no naming convention at all.
 *
 * TAG (MULTI) COUNTRY FIELDS ARE LOCKED TOO — but only in PASS 1 (2026-09-09).
 * The rule used to be "single-select only", which silently did nothing on
 * Skymark: their Country field (#25, shared by university/lead/deal) is stored
 * `valueTypes: "tags"`, so the Attach Course modal's Country sat empty and
 * editable next to a course whose university already declares "🇬🇧 UK", and
 * the Attach button stayed disabled on a field the operator could not
 * meaningfully answer. Tag-ness is about how a field is PICKED, not about
 * whether its value is knowable: a deal attached to one course sits in exactly
 * one country. The blast radius is exactly right because this whole function
 * no-ops unless a caller passes course countries, and the only callers that do
 * are the two "deal follows the picked course" surfaces (Attach Course modal,
 * Add Application). PASS 2 stays single-select-only — identifying a field by
 * its options alone is weak evidence, and a Tag field full of options is
 * exactly where it would misfire.
 *
 * Other rules (unchanged, and shared with the server):
 *   - PASS 2 is single-select only — never a Tag (multi) field;
 *   - a field the server already LOCKED (exactly one option) is left alone;
 *   - a field the server narrowed to SEVERAL options (a university with
 *     campuses in more than one country) is narrowed further by the given
 *     values — the Attach Course modal passes the Country picked in its
 *     left-hand filter, which is information the server never sees;
 *   - options match by trimmed, case-insensitive string ("Uk" ⇄ "UK");
 *     exactly one match ⇒ auto-fill + lock (a read-only box in the UI).
 */

export type CountryLockField = {
    id: number | string;
    name?: string | null;
    spesificTypes?: string | null;
    valueTypes?: string | null;
    options?: { id: number | string; value: string }[] | null;
};

const norm = (v: unknown) => String(v ?? "").trim().toLowerCase();

/** "country" / "countries" as a WORD inside the field's machine name. */
const COUNTRY_WORD = /(^|[^a-z])countr(y|ies)([^a-z]|$)/;

export function isCountryField(field: CountryLockField | null | undefined): boolean {
    if (!field) return false;
    if (norm(field.spesificTypes) === "country") return true;
    // `name` only — NEVER `label` (display overlay, see cfDisplayName).
    return COUNTRY_WORD.test(norm(field.name));
}

type Candidate = {
    prior: ResolvedField | undefined;
    next: ResolvedField;
};

export function bridgeCountryByValue(
    resolved: ResolvedField[],
    fields: CountryLockField[] | null | undefined,
    countryValues: string[] | null | undefined,
): ResolvedField[] {
    const values = new Set((countryValues ?? []).map(norm).filter((v) => v !== ""));
    if (!values.size || !fields?.length) return resolved;

    const priorById = new Map(
        resolved.map((r) => [Number(r.customInputId), r] as const),
    );

    /** The narrowing this field would get, or null when it gains nothing. */
    const candidateFor = (field: CountryLockField): Candidate | null => {
        const id = Number(field.id);
        const prior = priorById.get(id);
        // Already locked by the server — nothing to add.
        if (prior && (prior.resolvedOptions?.length ?? 0) === 1) return null;

        // Narrow the server's multi-option set when it has one; otherwise
        // start from the field's full option list.
        const pool =
            prior && (prior.resolvedOptions?.length ?? 0) > 1
                ? prior.resolvedOptions
                : (field.options ?? []).map((o) => ({
                      id: Number(o.id),
                      value: String(o.value),
                  }));
        const resolvedOptions = pool.filter((o) => values.has(norm(o.value)));
        if (!resolvedOptions.length) return null;
        if (prior && resolvedOptions.length === prior.resolvedOptions.length) {
            return null;
        }
        return {
            prior,
            next: {
                customInputId: id,
                resolvedOptions,
                autoFill: resolvedOptions.length === 1,
            },
        };
    };

    // PASS 1 — fields that DECLARE themselves as Country, Tag fields included
    // (see the note above: a deal attached to one course has one country).
    let picks = fields
        .filter(isCountryField)
        .map(candidateFor)
        .filter((c): c is Candidate => c !== null);

    // PASS 2 — nothing declared itself: identify the field by its OPTIONS.
    // Exactly one field, resolving to exactly one option, or we do nothing.
    // Single-select ONLY — an option match is weak evidence on its own.
    if (!picks.length) {
        const byOptions = fields
            .filter((f) => !isTagSelector(f) && !isCountryField(f))
            .map(candidateFor)
            .filter((c): c is Candidate => c !== null)
            .filter((c) => c.next.resolvedOptions.length === 1);
        if (byOptions.length === 1) picks = byOptions;
    }

    if (!picks.length) return resolved;

    const out = [...resolved];
    for (const { prior, next } of picks) {
        if (prior) out[out.indexOf(prior)] = next;
        else out.push(next);
    }
    return out;
}
